import type { AccountSummary, CategorySummary } from '@fruitful/types';
import { useAtomValue } from 'jotai/index';
import * as React from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyMediumEmphasized,
    ChevronLeftIcon,
    CloseButton,
    HolisticAccountLogo,
    Icon,
    isWeb,
    LabelEmphasized,
    ScrollView,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { getAllHolisticAccountsNotLinkedToGoals } from '../../../common/utils/goalUtils';
import { holisticSummaryAtom } from '../../../holistic/state/HolisticAtoms';
import { membershipGoalsAtom } from '../../state/GoalAtoms';

interface AccountListProps {
    onSelectAccount: (accountSummary: AccountSummary) => void;
    onDismiss: () => void;
    onBack?: () => void;
}

const AccountList = ({
    onSelectAccount,
    onDismiss,
    onBack,
}: AccountListProps) => {
    const insets = useSafeAreaInsets();

    const holisticSummary = useAtomValue(holisticSummaryAtom);
    const allHolisticAccounts = Object.keys(holisticSummary ?? {})
        .map((key) => (holisticSummary?.[key] as CategorySummary).accounts)
        .flat();

    const membershipGoals = useAtomValue(membershipGoalsAtom) ?? [];

    const availableHolisticAccounts = getAllHolisticAccountsNotLinkedToGoals(
        membershipGoals,
        allHolisticAccounts,
    );

    return (
        <YStack
            alignItems="center"
            justifyContent="center"
            marginHorizontal={16}
            maxHeight={isWeb ? '60vh' : 350}
        >
            <XStack
                alignItems="center"
                width="100%"
                justifyContent="space-between"
            >
                {onBack ? (
                    <XStack
                        alignItems="center"
                        justifyContent="center"
                        gap={8}
                        onPress={onBack}
                        paddingHorizontal={8}
                    >
                        <Icon icon={ChevronLeftIcon} size={16} />
                    </XStack>
                ) : (
                    <View width={30} height={30} />
                )}

                <BodyMediumEmphasized>
                    Pick the account to link
                </BodyMediumEmphasized>

                {onBack ? (
                    <View width={30} height={30} />
                ) : (
                    <CloseButton
                        backgroundColor="$gray200"
                        onPress={onDismiss}
                    />
                )}
            </XStack>
            <ScrollView
                width="100%"
                marginBottom={-insets.bottom}
                marginTop={isWeb ? 16 : 0}
                showsVerticalScrollIndicator={isWeb}
            >
                <YStack
                    gap={10}
                    paddingTop={20}
                    paddingBottom={insets.bottom * 2}
                >
                    {availableHolisticAccounts.map((linkedAccount) => {
                        const {
                            id,
                            accountNumberMasked,
                            institutionName,
                            logo,
                            name,
                        } = linkedAccount;

                        return (
                            <XStack
                                gap={10}
                                key={id}
                                onPress={() => {
                                    onSelectAccount(linkedAccount);
                                }}
                                animation="button"
                                pressStyle={{
                                    scale: 0.95,
                                }}
                            >
                                <HolisticAccountLogo
                                    institutionName={institutionName}
                                    logo={logo}
                                />
                                <YStack>
                                    <BodyMediumEmphasized>
                                        {name}
                                    </BodyMediumEmphasized>
                                    <LabelEmphasized color="$gray1000">
                                        {institutionName}
                                        {' *'}
                                        {accountNumberMasked}
                                    </LabelEmphasized>
                                </YStack>
                            </XStack>
                        );
                    })}
                </YStack>
            </ScrollView>
        </YStack>
    );
};

export default AccountList;
